/**
 * contract — the wall's wire contract (wall plan c, amendment "Method §4", `thinking/plans/
 * 2026_08_31_wall_plan_c_write_path_driver.md`; types as proposed by the board→back-end review,
 * `thinking/reports/2026_08_31_wall_board_backend_review.md` § 3 — written here near-verbatim per
 * that amendment's binding instruction). Every route under
 * `products/<house>/routes/{wall,tile.$id,mine,continue,continue.pack,seen}.ts` and every hook
 * plan d adds under `core/wall/ui` imports its types from here; one spec per route asserts the
 * JSON shape against a fixture (`__tests__/contract.spec.ts`).
 *
 * Wire casing is snake_case, as `wall.ts`/`mine.ts` already draw the boundary — camelCase lives
 * inside readers (`tiles.server.ts`, `mine.server.ts`, `arm.server.ts`); the routes map at the HTTP
 * edge, same split `toWallTile`/`WallTileRow` already draw.
 *
 * X2 (the review's blocking finding): `id` (the `generations` cuid primary key) is THE identifier
 * on every route below — `again`, `basis.take`, `seen.ids`, `parent_id`, `root_id` are all cuids.
 * The service UUID (`Generation.generationId`, the S3 key) never crosses the wire; `image_key` is
 * the picture.
 */

// ─── Bounds (single source; the routes validate against these, the UI caps against the same) ─────
export const MAX_DIRECTION_CHARS = 200 // = tsQuery.ts MAX_QUERY_LENGTH; keeps the return token under 2 048 (Y5)
export const MAX_SEEN_IDS = 100 // Y8
// Loose UUID-ish bound on `request_id`: Y5's "validated as a UUID (36 chars) so the column is
// bounded". Not a strict RFC4122 check — the DB column is a plain TEXT and a malformed-but-unique
// string is harmless; the point is bounding the column. Here rather than in `continue.pack.ts`
// because plan e's page verb mints the id on the client and carries it through the sign-in card,
// so the UI caps against the same bound the route validates against (the rule this block states).
export const MAX_REQUEST_ID_CHARS = 64
export const MINE_ROOT_CAP = 100 // the 100 newest chains (b amendment N1; plan h pages the rest)
export const MAX_CHAIN_TAKES = 50 // GET /chain/:root — the takes one column can carry (d)
export const WALL_PAGE_SIZE = 24
// Wall preview frames (`thinking/plans/2026_09_12_wall_preview_frames.md`, § Carrier "The contract"):
// the single source for both halves — `client.server.ts` divides `RENDER_STEPS` by this to get the
// generation request's `step_interval`, and the probe hook (`usePreviewFrames.ts`) uses it as the
// frame count to look for. Isomorphic here (not in `client.server.ts`, a `.server.ts` module the
// client-side probe hook cannot import) so both sides read the one constant.
export const WALL_PREVIEW_FRAMES = 3
export const RATE_LIMIT_WRITES_PER_MIN = 30 // per IP, in-memory per container (plan c § Decided)

export const RATIO_KEYS = ['1:1', '2:3', '3:4', '16:9', '9:16', '21:9'] as const // board ToileArmedScratch
export type RatioKey = (typeof RATIO_KEYS)[number]

// ─── The exhibit card ──────────────────────────────────────────────────────────────────────────
export type TileSummary = {
    id: number
    image_key: string
    width: number
    height: number
    pack_slug: string
    position: number // 0..8, the cell
    title: string
    chips: string[] // up to three labels, one per role: the house's list terms the tile carries (`scripts/wall/phrases/`)
    featured: boolean
}

/**
 * GET /wall, Ovid anonymous branch only (`2026_09_17_ovid_veiled_wall.md` § step 5) — the veiled
 * composition, or the veiled matches of a query, same bytes for every visitor and for Googlebot. NO
 * estate text anywhere on this type: no `pack_slug`, no `title`, no `chips` — the discriminated
 * union is the enforcement, not a convention, since a veiled tile's shape has no `image_key` field to
 * leak at all. `veil` is the tile's OWN veil, a same-origin picture path the house names with a keyed
 * hash (`products/ovid/wall/veilKey.server.ts`): opaque, so it ties the tile to no pack. No cursor, no
 * `matched_count` — the whole response IS the page, bounded by the reader that built it.
 */
export type AnonTile =
    | {id: number; width: number; height: number; image_key: string}
    | {id: number; width: number; height: number; veil: string}
export type AnonWallResponse = {
    tiles: AnonTile[]
    /** How many of `tiles`, from the front, are the featured composition — the rest is the bounded
     *  tail (ruling 4). A count, not estate text: it lets the client apply the composition-only
     *  "one 2x2 span per ~12" layout rule and the tail-only door-cell cadence without a boundary
     *  marker baked into the tile shape itself (which the plan's own literal field list — "id,
     *  width, height, veil" — has no room for). Added beyond that literal list because step 6's
     *  layout rules are not otherwise derivable from the response at all; flagged as a deliberate
     *  addition in the implementation report. A search answer is all composition (every match, no
     *  coda): the count is `tiles.length`. */
    composition_count: number
}

// GET /wall?q=&cursor= — anonymous, pure function of its URL; Cache-Control: public, max-age=60,
// s-maxage=60. Never 4xx: a malformed cursor reads the first page of the segment `q` implies.
export type WallQuery = {q?: string; cursor?: string}
export type WallResponse = {
    tiles: TileSummary[]
    next_cursor: string | null
    seam: boolean // true once per search; with an empty q it is routing, never a ghost (d N13)
    matched_count?: number // seam page of a non-empty q only
}

// GET /tile/:id — anonymous, pure; public, max-age=60 on 200 and on 404 {error:'not_found'} (Y10).
export type TileResponse = TileSummary

// ─── The user's rows ───────────────────────────────────────────────────────────────────────────
export type TakeStatus = 'queued' | 'rendering' | 'done' | 'failed'
export type FailReason =
    | 'timeout'
    | 'service_error'
    | 'non_compliant'
    | 'training_failed'
    | 'identity_unavailable'
    | 'reclaimed_max' // amendment Y3 names it; `reclaimStale` terminates a row reclaimed 3 times
    | 'refused' // the direction rewriter declined; the pill line is per house (`ui/theme.ts`'s `verbs.refused`)
    | 'unknown'

export type Basis = {tile: number} | {take: string} | null // null = the house baseline (the ghost)

export type Take = {
    id: string // generations.id (cuid) — THE identifier on every route (X2)
    status: TakeStatus
    fail_reason: FailReason | null
    image_key: string | null // null until done (Y2)
    width: number
    height: number
    direction: string // '' when none (B2/N5)
    basis: Basis
    parent_id: string | null // = basis.take when the parent is a take
    root_tile_id: number | null // null = a from-scratch chain
    root_id: string // rootGenerationId — itself on every first-in-chain row (Y11)
    root: TileSummary | null // joined; the pill's slot 1 and name (B1); null when from scratch
    batch_id: string | null
    position: number | null // 0..8 inside a batch
    muse_slug: string | null // Ovid provenance; never null at Ovid (Y7)
    created_at: string // ISO 8601
    seen_at: string | null
    // `generations.pinnedAt` (plan `2026_09_04_wall_pin.md` § Server) — the clock a pinned cell is
    // placed by on the wall (`cells.ts`'s day rule), null when the take is not pinned. On every
    // route serving a `Take`, since the column's door reads it wherever a chain is armed, not only
    // from `/mine`.
    pinned_at: string | null
}

/**
 * A structural check, not a type assertion — a 2xx body is untrusted input regardless of what the
 * caller's own `as Take`/`: ContinueResponse` annotation claims (error-handling review F3, the same
 * posture `direction.server.ts`'s own `isValidBody` already takes for the pontifex wire). Checks the
 * fields three client readers actually dereference (`Wall.tsx`'s `mergeTake`/`recordTakes`/
 * `applyPinnedTake`, `useMine.ts`'s `withTake`/`withPin`, `chains.ts`'s `chainKeyOf`/`at`) — a proxy
 * or edge-worker rewriting the response, or a partial deploy serving an older shape, produces a
 * caught failure here rather than a `TypeError` inside a React state updater, where nothing catches
 * it (`SeenResponse`'s own guard, `Wall.tsx`'s `stampSeen`, is the shipped precedent this follows).
 * Not a full schema validation — `basis`, `muse_slug` and the rest are read defensively by their own
 * callers or not read by the client at all.
 */
export function isTake(value: unknown): value is Take {
    if (typeof value !== 'object' || value === null) return false
    const v = value as Record<string, unknown>
    if (typeof v.id !== 'string' || v.id.length === 0) return false
    if (typeof v.status !== 'string') return false
    if (v.root_tile_id !== null && typeof v.root_tile_id !== 'number') return false
    if (typeof v.root_id !== 'string') return false
    if (typeof v.created_at !== 'string') return false
    if (v.seen_at !== null && typeof v.seen_at !== 'string') return false
    if (v.pinned_at !== null && typeof v.pinned_at !== 'string') return false
    return true
}

export type Identity =
    | {kind: 'none'} // Toile
    // Narcis: `eta_ms` the training remainder (null unless training, or with no sample to estimate
    // from); `thumb_key` the accepted intake photo while training, the earliest done wall row once
    // ready; `name` the account's initial. `percent` (wall review follow-ups unit 4) is the SAME
    // checkpointStep/targetSteps reading `/api/model-status`'s `ModelStatusProvider` used to poll
    // separately for — `checkpointPercent` (`@core/products/trainingProgress`), off the identical
    // live job row `eta_ms` is computed from. Null whenever there is no live job to read it off
    // (`state !== 'training'`); unlike `eta_ms`, it never goes null FOR a null-sample reason once
    // a live job exists — the formula needs no median, only the job's own step counters.
    | {kind: 'model'; state: 'missing' | 'training' | 'ready'; eta_ms: number | null; percent: number | null; thumb_key: string | null; name: string | null}
    | {kind: 'muse'; slug: string | null; name: string | null; thumb?: string | null}
//   Ovid: slug null = none chosen (the column opens in the choose state); `thumb` the chosen muse's picture

/**
 * Where a signed-in reader stands against a house's picture allowance (Ovid): pictures left in the rolling window, the
 * number held against, whether a plan sets it, and when the next one frees (ISO instant; null while any are left).
 * Absent from `/mine` at a house with no allowance. A `402 allowance-exhausted` answers `limit`, `hasPlan` and
 * `resetsAt` of the same state, with none left.
 */
export type AllowanceState = {remaining: number; limit: number; hasPlan: boolean; resetsAt: string | null}

// GET /mine?q= — signed-in; Cache-Control: private, no-store on every branch including 401.
// `done`: the latest done row per chain (root_tile_id, or root_id for scratch), newest chain first,
// <= MINE_ROOT_CAP. `pending`: every queued|rendering row, plus every failed row whose seen_at is
// null (the pill says `failed` once; POST /seen stamps it).
export type MineResponse = {
    done: Take[]
    pending: Take[]
    eta_ms: number | null // the pending set: house median x pending rows (+ training remainder at Narcis)
    identity: Identity
    // `q` matches (`2026_09_01_search_own_chains.md`) — every chain (armed or from-scratch) with a
    // row whose tsv (direction ∥ served prompt) matches, newest chain first, shown by its current
    // `done` take. Present only when the request carried a non-empty `q` (`[]` counts as present —
    // a search that matched nothing); absent entirely on an empty `q`, which is the unchanged shape
    // this response carried before this field existed. The wall leads its matched segment with
    // these before the catalog's own `tsv @@ q` tiles (`GET /wall`, unrelated to this field).
    matched?: Take[]
    // Every own row with `pinnedAt IS NOT NULL` (plan `2026_09_04_wall_pin.md` § Server), newest
    // `pinnedAt` first, capped at `MINE_ROOT_CAP`, joined to root tiles like `done`/`pending`. NOT
    // gated on `q` (unlike `matched` above) — a pin is findable whether or not the field is empty,
    // so the empty-`q` response is byte-for-byte what it was before this field existed, plus this
    // one always-present array (`[]` for a user with no pins). `cells.ts` places one cell per entry
    // here, skipped when the take is currently its chain's face (`ownDone`'s own row for that chain).
    pinned: Take[]
    allowance?: AllowanceState
}

// GET /mine?tiles=1,2,3 — signed-in, same headers as `/mine` (`2026_09_24_mine_beyond_the_cap.md`).
// The viewer's own chains rooted on the named tiles (1..WALL_PAGE_SIZE ids, one loaded page's
// worth): the newest `done` row per chain, whether or not that chain is among `/mine`'s
// MINE_ROOT_CAP newest. Pending rows are not repeated here — `/mine.pending` is not capped by chain
// and is the one polled source for them. A malformed `tiles` is a 400 `invalid_body`, field `tiles`.
export type MineTilesResponse = {
    done: Take[]
}

// GET /chain/:root — signed-in; Cache-Control: private, no-store on every branch including 401/404.
// ONE chain's takes, oldest first, at most MAX_CHAIN_TAKES (the newest kept). `:root` is the chain
// key `chainRoot.ts` names — digits = `root_tile_id`, anything else = `root_id`. Asked once when a
// chain is ARMED, never carried by `/mine` (whose cost must not grow with a chain's depth): the
// column's counter reads the page session's ledger of rows it has seen, and a reload starts that ledger
// empty. `404 {error:'not_found'}` when the caller owns no row of that chain — an unknown chain and
// another user's chain are indistinguishable from outside.
export type ChainResponse = {takes: Take[]}

// ─── Writes (signed-in; requireUser; per-IP rate limit; private, no-store on every response) ─────
// POST /continue — a discriminated union; both shapes present or neither -> 400 invalid_body.
//   {basis, direction}: the named node becomes the parent.
//   {again}: a sibling — the server copies parent_id, direction, width/height, muse_slug, root ids
//   from `again` (same user; another user's id -> 404 not_found). `request_id`: optional UUID,
//   position 0, idempotent per user.
//   `ratio` (optional on BOTH shapes since the operator decision of 2026-08-31, which supersedes
//   "only from the ghost does Toile ask" — head §4's "ratio only with basis null" is withdrawn
//   with it): the user may change the shape at any point of a chain, so a ratio overrides the
//   dimensions the row would otherwise inherit from its basis. Omitted, the inheritance stands
//   (basis tile's, parent take's, or the house default from scratch) — the client sends a ratio
//   only when the user actually picked one. An `again` with a ratio is still a sibling: same
//   parent, same direction, new shape.
export type ContinueRequest =
    | {basis: Basis; direction: string; ratio?: RatioKey; request_id?: string}
    | {again: string; ratio?: RatioKey; request_id?: string}
export type ContinueResponse = {take: Take; created: boolean} // 200 on a replay too, created:false

// POST /continue/pack — nine rows in one statement, UNIQUE (userId, requestId, position) (X4).
export type ContinuePackRequest = {pack_slug: string; direction: string; request_id: string}
export type ContinuePackResponse = {batch_id: string; takes: Take[]; created: boolean} // position order

// POST /seen — stamps seen_at on the caller's done and failed rows among `ids`; <= MAX_SEEN_IDS.
export type SeenRequest = {ids: string[]}
export type SeenResponse = {stamped: number}

// POST /pin — pins or unpins the caller's own DONE row (plan `2026_09_04_wall_pin.md` § Server),
// the same shape and guards as `/seen`: signed-in, own row only, product-bound, 2xx on success,
// idempotent (a repeat of the same `on` changes nothing — the clock does not move on a retry).
// Sets `pinnedAt` to now when `on` is true, clears it when false. `take` returns the row's own
// current state, so the caller folds it straight into `/mine`'s shape the way `POST /continue`'s
// reply already does (`useMine.ts`'s `mergeTake`).
export type PinRequest = {take: string; on: boolean}
export type PinResponse = {take: Take}

// POST /share — stamps `generations.sharedAt` on the caller's own DONE continue row (plan
// `2026_10_25_wall_share.md` § 8): the take page `/take/<generationId>` resolves only once this is set.
// The stamp is the first share's time and is never re-stamped. Not served at Ovid (keep-only).
export type ShareRequest = {take: string}
export type ShareResponse = {shared_at: string}

// POST /mine/muse — Ovid only (wall verbs plan § Decided 6): `{slug}` validated against the muse
// registry, writes `User.ovidMuseSlug`, returns the caller's own new `Identity` — the same shape
// `GET /mine` resolves, so the client folds the response into its held state with no second poll.
export type MuseChooseRequest = {slug: string}
export type MuseChooseResponse = {identity: Identity}

// ─── Errors ────────────────────────────────────────────────────────────────────────────────────
export type ErrorCode =
    | 'not_signed_in' // 401
    | 'invalid_body' // 400, with `field`
    | 'not_found' // 404 (tile, take of another user, unknown pack_slug)
    | 'rate_limited' // 429, with Retry-After
    | 'identity_required' // 409 — no identity chosen (g N11; Ovid `/continue`'s own new-chain
                          // museless refusal, wall verbs plan § Decided 2, answers this same code —
                          // one concept, one wire value, house-agnostic)
    | 'conflict' // 409 — request_id owned by another user (X4)
    | 'credits_required' // 402 — `HouseAdapter.canWrite` refused the write
    | 'allowance-exhausted' // 402 — Ovid's picture allowance is spent; answers `limit`, `hasPlan` and `resetsAt` beside it
    | 'internal_error' // 500 — the server's own doing, never the caller's malformed body (B8/F2)
export type ErrorResponse = {error: ErrorCode; field?: string}

// ─── The return token — the wall's own URL after sign-in (d §10, e S4) ────────────────────────────
// Carried as the `redirectTo` FORM field of the sign-in card (SignInCard.tsx:241,255), sanitised by
// redirectTarget.server.ts (<= 2 048 chars after normalisation). The auth callback appends
// auth-event, auth-method, trigger; the wall reads once, re-arms only (d N7), and strips every key
// below.
export type ReturnToken = {
    basis?: string // tile id, digits only (an anonymous visitor can only have armed a tile or the ghost)
    direction?: string // <= MAX_DIRECTION_CHARS
    ratio?: RatioKey
    batch?: string // after the page verb: lift the nine, open the pill, no column
    pack?: string // Ovid museless page verb: the wall submits after "Choose her." (g)
    rid?: string // request_id minted before the redirect (e S4); the page re-submits with it
    render?: '1' // the card was raised by a Render press: the armed chain renders once on return
}
export const RETURN_TOKEN_KEYS = ['basis', 'direction', 'ratio', 'batch', 'pack', 'rid', 'render'] as const
export const AUTH_CALLBACK_KEYS = ['auth-event', 'auth-method', 'trigger'] as const
export const GATE_TRIGGER_WALL = 'wall' // ?gate-trigger= the wall pushes when it opens SignInCard (Y5)
export const GATE_TRIGGER_SHARE = 'share' // the Share door on a visitor's free take (first-take-free plan)
export const GATE_TRIGGER_PAGE_VERB = 'page_verb' // the pack page's own verb (e § GA4)

// ─── GA4 (typed here so both sides agree; plan d wires the emitters) ──────────────────────────────
export type WallGenerateKind = 'single' | 'again' | 'from_ghost' | 'pack'
// 'hover' (a from-scratch cell's hover-Generate door) dropped 2026-09-18: its one producer,
// `handleGenerateNow`'s `arm(target, 'hover')`, was removed in commit 0ed03020f, and nothing else in
// `app/core/wall` ever read or produced it — see growth/ga4_manifest.py's `from` Dim for the GA4 side
// of this retirement (rows collected before that date still carry the value).
// 'request': Enter on a search of `REQUEST_MIN_WORDS` typed words armed the ghost with those words.
// A 'request' arm carries `via` (`WallMakeVia`): which press asked for it.
export type WallArmedFrom = 'tap' | 'ghost' | 'page' | 'request'
export type WallMakeVia = 'enter' | 'cmd_enter' | 'make_row'
// How a settled search began (`wall_search.from`): typed words, or a phrase, a facet value, a Recent row or a
// "with these" chip taken from the panel or from a tile.
export type WallSearchFrom = 'typed' | 'phrase' | 'facet' | 'recent' | 'with'
// A word door tapped (board `WordsAsDoors`, validated 2026-09-18) — the armed caption/sheet cap, or
// an unarmed tile's hover label.
export type WallWordTappedFrom = 'caption' | 'hover'
// A chip phrase set or lifted: on a tile chip (the armed caption or an unarmed tile's hover label) or on
// the pill's own × in the search bar. The phrase is what a viewer profile will weigh.
export type WallChipTappedFrom = 'chip_caption' | 'chip_hover' | 'pill'

/** A search of this many typed words or more is read as an image request, not as search words: on
 *  Toile and Narcis, 2026-08-24..10-29, every such search was a whole request and every search
 *  proper was 1-3 words. Pills do not count. */
export const REQUEST_MIN_WORDS = 8

/** Whether typed words (`parseQuery(q).words`, pills left out) read as a whole image request — by word
 *  count alone, so any language that spaces its words, never by content. */
export function looksLikeRequest(words: string): boolean {
    const t = words.trim()
    return t.length > 0 && t.split(/\s+/).length >= REQUEST_MIN_WORDS
}
